/**
 * useStaffProfiles - live Entra profiles (name, title, department, photo) for a set of staff
 * UPNs, from GET /v3/config/staff-directory (Microsoft Graph behind the API; any staff member,
 * not only pilot users). One request per new batch of UPNs; results are cached for the page
 * load and shared across widgets. Bill 2026-10-13.
 */
import { useEffect, useMemo, useState } from 'react';
import type { StaffDirectoryEntry } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';

const cache = new Map<string, StaffDirectoryEntry | null>();
const inflight = new Map<string, Promise<void>>();

function norm(u: string | null | undefined): string {
  return (u ?? '').trim().toLowerCase();
}

export function useStaffProfiles(upns: ReadonlyArray<string | null | undefined>): ReadonlyMap<string, StaffDirectoryEntry | null> {
  const client = useApiClient();
  // Keyed on the contents, not the array identity (callers rebuild the array every render).
  const key = Array.from(new Set(upns.map(norm).filter((u) => u.endsWith('@mypcb.com')))).sort().join(',');
  const wanted = useMemo(() => (key ? key.split(',') : []), [key]);
  const [, bump] = useState(0);

  useEffect(() => {
    let live = true;
    const missing = wanted.filter((u) => !cache.has(u) && !inflight.has(u));
    const waits = wanted.filter((u) => inflight.has(u)).map((u) => inflight.get(u)!);
    if (missing.length > 0) {
      const p = client.retail.config
        .staffDirectory(missing)
        .then((r) => {
          const data = ((r as { data?: unknown }).data ?? {}) as Record<string, StaffDirectoryEntry>;
          for (const u of missing) cache.set(u, data[u] ?? null);
        })
        .catch(() => { for (const u of missing) cache.set(u, null); })
        .finally(() => { for (const u of missing) inflight.delete(u); });
      for (const u of missing) inflight.set(u, p);
      waits.push(p);
    }
    if (waits.length > 0) void Promise.all(waits).then(() => { if (live) bump((n) => n + 1); });
    return () => { live = false; };
  }, [client, wanted]);

  return useMemo(() => {
    const m = new Map<string, StaffDirectoryEntry | null>();
    for (const u of wanted) if (cache.has(u)) m.set(u, cache.get(u) ?? null);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, cache.size]);
}

export function profileFor(
  profiles: ReadonlyMap<string, StaffDirectoryEntry | null>,
  upn: string | null | undefined,
): StaffDirectoryEntry | null {
  return profiles.get(norm(upn)) ?? null;
}
